import invariant from 'tiny-invariant';
import {
  DEFAULT_PAGE_SIZE,
  StorageAdapter,
  StorageCapabilities,
  FeedItemUrlMap,
  StorageItemId,
} from '../common/adapter';
import { StorageGetNextListPage, StorageListPage } from '../common/list-page';
import { StorageManager } from '../common/manager';
import { MockItem } from '../common/mock.item';
import { MockListPage } from '../common/mock.list-page';
import { GoogleDriveItem } from './google-drive.item';
import { GoogleDriveListPage } from './google-drive.list-page';
import {
  BasicStorageItem,
  getUploadMimeType,
  Logger,
  ServiceProvider,
  StorageFolder,
  StorageItem,
} from '@uploadcam/common';
import { StorageHttpError } from '../common/errors';
import { GetCredentialInteractor } from '../../../auth/domain/interactors/get-credential.interactor';
import { GOOGLE_DRIVE_FULL_ACCESS_SCOPE } from '../../../auth-google/auth-google.provider';
import { FriendlyError } from '../../../../app/core/utils';

export const GOOGLE_DRIVE_ROOT = 'uploadcam://google-drive';
export const GOOGLE_DRIVE_MY_DRIVE_ID = 'uploadcam://google-drive/my-drive';
export const GOOGLE_DRIVE_SHARED_DRIVES_ID = 'uploadcam://google-drive/shared-drives';
export const GOOGLE_DRIVE_SHARED_WITH_ME_ID = 'uploadcam://google-drive/shared-with-me';

const THUMBNAIL_SIZE = 320;
const PREVIEW_SIZE = 1600;

export class GoogleDriveAdapter implements StorageAdapter {
  private readonly fileFields =
    'id, name, parents, mimeType, createdTime, modifiedTime, shortcutDetails';
  private readonly drive: typeof gapi.client.drive;
  private manager!: StorageGetNextListPage;

  constructor(
    private readonly getCredential: GetCredentialInteractor,
    private readonly googleApi: typeof gapi,
    private readonly logger: Logger,
    private readonly teamRoot?: StorageFolder,
  ) {
    this.drive = googleApi.client.drive;
  }

  public setManager(manager: StorageManager): void {
    this.manager = manager as unknown as StorageGetNextListPage;
  }

  public getCapabilities(): StorageCapabilities {
    return {
      previewThumbnails: true,
      userGroups: true,
    };
  }

  public getProvider(): ServiceProvider {
    return ServiceProvider.Google;
  }

  public async getRootFolder(): Promise<StorageItem> {
    return MockItem.rootFolder(GOOGLE_DRIVE_ROOT, ServiceProvider.Google, undefined, false);
  }

  public async getDownloadUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    const authuser = this.getCredential.execute().metadata['authuser'] ?? 0;

    return Object.fromEntries(
      items.map(({ id }) => {
        return [id, `https://drive.google.com/uc?export=download&authuser=${authuser}&id=${id}`];
      }),
    );
  }

  public async getFileUrl(file: StorageItem): Promise<string> {
    const authuser = this.getCredential.execute().metadata['authuser'] ?? 0;

    return `https://drive.google.com/file/d/${file.id}/view?usp=sharing&authuser=${authuser}`;
  }

  public async getFolderUrl(folder: StorageItem): Promise<string> {
    const authuser = this.getCredential.execute().metadata['authuser'] ?? 0;

    return `https://drive.google.com/drive/u/${authuser}/folders/${folder.id}`;
  }

  public getProviderUrl(): string {
    const authuser = this.getCredential.execute().metadata['authuser'] ?? 0;

    return `https://drive.google.com/drive/u/${authuser}/home`;
  }

  public async getThumbnailUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    const credential = this.getCredential.execute();
    const authuser = credential.metadata['authuser'] ?? 0;

    if (credential.scopes.includes(GOOGLE_DRIVE_FULL_ACCESS_SCOPE)) {
      return this.getImageUrls(items, THUMBNAIL_SIZE);
    } else {
      return GoogleDriveAdapter.getHackThumbnailUrlsFn(items, authuser);
    }
  }

  public async getPreviewUrls(items: BasicStorageItem[]): Promise<FeedItemUrlMap> {
    const credential = this.getCredential.execute();
    const authuser = credential.metadata['authuser'] ?? 0;

    if (credential.scopes.includes(GOOGLE_DRIVE_FULL_ACCESS_SCOPE)) {
      return this.getImageUrls(items, PREVIEW_SIZE);
    } else {
      return GoogleDriveAdapter.getHackPreviewUrlsFn(items, authuser);
    }
  }

  /**
   * This method only works if the user has full access to Google Drive.
   */
  private async getImageUrls(items: BasicStorageItem[], size?: number): Promise<FeedItemUrlMap> {
    if (items.length === 0) {
      return {};
    }

    const batch = this.googleApi.client.newBatch();

    items.forEach((item, idx) => {
      batch.add(
        gapi.client.drive.files.get({
          fileId: item.id,
          fields: 'thumbnailLink',
          supportsAllDrives: true,
        }),
        { id: idx } as any,
      );
    });

    const { result } = await batch;

    return Object.fromEntries(
      items.map((item, idx) => {
        const thumbnailLink = result[idx]?.result?.thumbnailLink;

        if (!thumbnailLink) {
          return [item.id, undefined];
        }

        return [item.id, size ? thumbnailLink.replace('=s220', `=s${size}`) : thumbnailLink];
      }),
    );
  }

  private static getImageUrlsHack(
    items: BasicStorageItem[],
    size: number,
    authuser?: number,
  ): FeedItemUrlMap {
    const params = [...(authuser ? [`authuser=${authuser}`] : []), `sz=w${size}`];

    return Object.fromEntries(
      items.map(({ id }) => {
        return [id, `https://drive.google.com/thumbnail?${[...params, `id=${id}`].join('&')}`];
      }),
    );
  }

  public static getHackThumbnailUrlsFn(
    items: BasicStorageItem[],
    authuser?: number,
  ): FeedItemUrlMap {
    return GoogleDriveAdapter.getImageUrlsHack(items, THUMBNAIL_SIZE, authuser);
  }

  public static getHackPreviewUrlsFn(items: BasicStorageItem[], authuser?: number): FeedItemUrlMap {
    return GoogleDriveAdapter.getImageUrlsHack(items, PREVIEW_SIZE, authuser);
  }

  public updateAccessToken(token: string): void {
    this.googleApi.client.setToken({ access_token: token });
  }

  // Thanks: https://stackoverflow.com/a/74011137/379923
  public async write(parent: StorageItem, file: File): Promise<StorageItem> {
    const createUploadUrl = (fileId: string, params: Record<string, boolean>) => {
      const uploadUrl = new URL(`https://www.googleapis.com/upload/drive/v3/files/${fileId}`);

      Object.entries({ ...params, uploadType: 'media' }).map(([key, value]) =>
        uploadUrl.searchParams.append(key, `${value}`),
      );

      return uploadUrl;
    };

    const params = {
      enforceSingleParent: true,
      supportsAllDrives: true,
    };
    const mimeType = getUploadMimeType(file);

    // Create file handle
    const { result } = await this.googleApi.client.drive.files.create(params, {
      name: file.name,
      mimeType,
      parents: [parent.id],
    });

    // Upload the file data
    const res = await fetch(createUploadUrl(result.id!, params), {
      method: 'PATCH',
      headers: new Headers({
        Authorization: `Bearer ${this.googleApi.client.getToken().access_token}`,
        'Content-Type': mimeType,
      }),
      body: file,
    });

    if (!res.ok) {
      // The handle above already exists in Drive but holds no bytes. Leaving it
      // behind would show up as a 0-byte photo in the album (and make the file
      // look uploaded), so drop it before reporting the failure upstream.
      await this.deleteQuietly(result.id!, params.supportsAllDrives);

      throw new StorageHttpError(
        res.status,
        `Google Drive rejected the upload of "${file.name}" (HTTP ${res.status})`,
        await res.text().catch(() => undefined),
      );
    }

    return new GoogleDriveItem(result);
  }

  /** Best-effort cleanup: never let it mask the error that triggered it. */
  private async deleteQuietly(fileId: string, supportsAllDrives: boolean): Promise<void> {
    try {
      await this.drive.files.delete({ fileId, supportsAllDrives });
    } catch (error) {
      this.logger.warn(`Could not remove the empty file handle "${fileId}"`, error);
    }
  }

  public async createFolder(parent: GoogleDriveItem, name: string): Promise<GoogleDriveItem> {
    invariant(parent.id !== GOOGLE_DRIVE_ROOT, 'Cannot create folder at root');

    const res = await this.drive.files.create({
      fields: this.fileFields,
      resource: {
        name,
        mimeType: 'application/vnd.google-apps.folder',
        parents: [parent.id === GOOGLE_DRIVE_MY_DRIVE_ID ? 'root' : parent.id],
      },
      supportsAllDrives: true,
    });

    return new GoogleDriveItem(res.result);
  }

  public async renameFolder(folder: GoogleDriveItem, name: string): Promise<GoogleDriveItem> {
    // Google Drive allows sibling folders with the same name, so we explicitly
    // check for a conflict instead of relying on the provider to reject it.
    await this.assertNoFolderNameConflict(folder, name);

    const res = await this.drive.files.update({
      fileId: folder.id,
      fields: this.fileFields,
      supportsAllDrives: true,
      resource: { name },
    });

    return new GoogleDriveItem(res.result);
  }

  private async assertNoFolderNameConflict(folder: GoogleDriveItem, name: string): Promise<void> {
    const parentId = folder.parentId;

    // Without a known parent we can't scope the lookup; skip the check and let
    // the rename proceed rather than block on an ambiguous query.
    if (!parentId) {
      return;
    }

    // Escape backslashes first, then single quotes, for the Drive query string.
    const escapedName = name.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    const res = await this.drive.files.list({
      fields: 'files(id)',
      q:
        `'${parentId}' in parents and name = '${escapedName}' ` +
        `and mimeType = 'application/vnd.google-apps.folder' and trashed = false`,
      includeItemsFromAllDrives: true,
      supportsAllDrives: true,
      pageSize: 1,
    });

    const hasConflict = (res.result.files ?? []).some((file) => file.id !== folder.id);

    if (hasConflict) {
      throw new FriendlyError(
        'folderNameConflict',
        new Error(`A folder named "${name}" already exists.`),
      );
    }
  }

  private async listFolderFiles(
    query: string,
    onlyFolders: boolean = false,
  ): Promise<StorageListPage> {
    const onlyFoldersFilter = "and mimeType='application/vnd.google-apps.folder'";
    const req = {
      fields: `nextPageToken, files(${this.fileFields})`,
      orderBy: 'folder, name',
      q: `${query} and trashed = false ${onlyFolders ? onlyFoldersFilter : ''}`,
      includeItemsFromAllDrives: true,
      supportsAllDrives: true,
      pageSize: DEFAULT_PAGE_SIZE,
    };

    const res = await this.drive.files.list(req);

    return new GoogleDriveListPage(this.manager, req, res.result, onlyFolders);
  }

  public async listFolder(
    item: GoogleDriveItem,
    onlyFolders: boolean = false,
  ): Promise<StorageListPage> {
    if (item.id === GOOGLE_DRIVE_ROOT) {
      return new MockListPage([
        ...(this.teamRoot ? [MockItem.fromStorageFolder(this.teamRoot, true, 'Team Root')] : []),
        MockItem.folder(GOOGLE_DRIVE_MY_DRIVE_ID, ServiceProvider.Google, undefined, 'My Drive'),
        MockItem.folder(
          GOOGLE_DRIVE_SHARED_DRIVES_ID,
          ServiceProvider.Google,
          undefined,
          'Shared Drives',
          false,
        ),
        MockItem.folder(
          GOOGLE_DRIVE_SHARED_WITH_ME_ID,
          ServiceProvider.Google,
          undefined,
          'Shared With Me',
          false,
        ),
      ]);
    }

    switch (item.id) {
      case GOOGLE_DRIVE_MY_DRIVE_ID:
        return this.listFolderFiles(`'root' in parents`, onlyFolders);

      case GOOGLE_DRIVE_SHARED_DRIVES_ID:
        // TODO: Handle `nextPageToken`
        const res = await this.drive.drives.list({
          fields: 'nextPageToken, drives(id, name)',
          pageSize: DEFAULT_PAGE_SIZE,
        });

        return new MockListPage(
          (res.result.drives ?? []).map((drive) =>
            MockItem.folder(drive.id!, ServiceProvider.Google, drive.id!, drive.name!),
          ),
        );

      case GOOGLE_DRIVE_SHARED_WITH_ME_ID:
        return this.listFolderFiles(`sharedWithMe`, onlyFolders);

      default:
        return this.listFolderFiles(`'${item.id}' in parents`, onlyFolders);
    }
  }

  public async moveItems(
    targetFolder: StorageItem,
    items: StorageItem[],
  ): Promise<Record<StorageItemId, boolean>> {
    if (items.length === 0) {
      return {};
    }

    const batch = this.googleApi.client.newBatch();
    const itemsToMove = items.filter((item) => !!item.parentId);

    itemsToMove.forEach((item) => {
      batch.add(
        this.drive.files.update({
          fileId: item.id,
          addParents: targetFolder.id,
          removeParents: item.parentId,
          supportsAllDrives: true,
          fields: 'id',
          resource: {},
        }),
        // Use item.id as the key for easier lookup
        { id: item.id } as any,
      );
    });

    const batchResult = itemsToMove.length > 0 ? await batch : undefined;
    const results: Record<StorageItemId, boolean> = {};

    for (const item of items) {
      const response = batchResult?.result[item.id];
      results[item.id] = !!response && response.status === 200 && !!response.result.id;
    }

    return results;
  }

  public async trashItems(items: StorageItem[]): Promise<Record<StorageItemId, boolean>> {
    if (items.length === 0) {
      return {};
    }

    // Initialize all as failed, update on success
    const result: Record<StorageItemId, boolean> = {};
    items.forEach((item) => (result[item.id] = false));

    try {
      const batch = this.googleApi.client.newBatch();

      items.forEach(({ id }) => {
        // Use item.id as the key for easier lookup
        batch.add(
          this.drive.files.update({
            fileId: id,
            supportsAllDrives: true,
            resource: { trashed: true },
          }),
          {
            id,
          } as any,
        );
      });

      const batchResponse = await batch;
      const batchResult = batchResponse.result;

      for (const itemId in batchResult) {
        if (batchResult.hasOwnProperty(itemId)) {
          const responseItem = batchResult[itemId];

          result[itemId as StorageItemId] =
            !!responseItem && responseItem.status === 200 && !responseItem.result.error;
        }
      }
      return result;
    } catch (error) {
      this.logger.error('Google Drive batch delete failed:', error);
      // On a full batch failure, the initial 'false' values will be returned.
      return result;
    }
  }

  public async getNextFolderPage(
    onlyFolders: boolean,
    options: {
      req: Record<string, unknown>;
      pageToken: string | undefined;
    },
  ): Promise<StorageListPage> {
    const res = await this.drive.files.list({
      ...options.req,
      pageToken: options.pageToken,
    });

    return new GoogleDriveListPage(this.manager, options.req, res.result, onlyFolders);
  }

  public async setCaption(item: Readonly<StorageItem>, caption?: string): Promise<void> {
    await this.drive.files.update({
      fileId: item.id,
      supportsAllDrives: true,
      resource: {
        description: caption,
      },
    });
  }
}
